import {WSConfig, WSEvent, WSSubscription} from './models'

class EventWrapper<E> {
  constructor(
    public event: E,
    public date: Date = new Date()
  ) {}
}

export interface WsUser {
  id: string
  token: string
}

export class WsClient {
  private readonly initUrl: string
  private readonly connectUrl: string
  private enableDebug: boolean = false

  protected lastInWSEvent: EventWrapper<WSEvent<unknown, unknown>> | null = null
  protected lastOutWSEvent: EventWrapper<WSEvent<unknown, unknown>> | null =
    null
  protected lastCloseEvent: EventWrapper<CloseEvent> | null = null

  private ws?: WebSocket
  private user?: WsUser

  onWSClose?: (ev: CloseEvent) => void
  onWSError?: (ev: Event) => void
  onWSMessage?: (ev: MessageEvent) => void
  onWSOpen?: (ev: Event) => void
  reconnectHandler?: (
    client: WsClient
  ) => (WsUser | false) | Promise<WsUser | false>
  private reconnectRetries = 0
  private reconnectRetriesLimit = 6

  private subscriptions: Array<WSSubscription<unknown, unknown>> = []

  constructor(protected config: WSConfig) {
    // SETUP CONNECTION
    const [HTTP, WS] = this.getProtocols()

    this.initUrl = `${HTTP}://${this.config.host}/${config.initPath}`
    this.connectUrl = `${WS}://${this.config.host}/${config.connectPath}`
  }

  private getProtocols(): [string, string] {
    return this.config.ssl && !this.config.host.startsWith('localhost')
      ? ['https', 'wss']
      : ['http', 'ws']
  }

  private async authenticate(token: string): Promise<void> {
    const response = await fetch(this.initUrl, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      credentials: 'include'
    })
    if (!response.ok) throw new Error(`Failed to connect: ${response.status}`)
  }

  async connect(user: WsUser, enableDebug = false) {
    this.user = user
    this.enableDebug = enableDebug
    if (this.enableDebug) {
      console.log(this)
    }
    return this.doConnect()
  }

  private async doConnect(): Promise<void> {
    const token = this.user?.token
    if (!token) return Promise.reject('No token')

    await this.authenticate(token)

    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.connectUrl)
      const timeoutId = setTimeout(() => {
        ws.close(4408, 'Timed out')
        reject('WS connect timeout')
      }, 5000)

      ws.onopen = (ev: Event) => {
        clearTimeout(timeoutId)
        this.ws = ws
        this.onopen(ev)
        ws.onclose = (ev: CloseEvent) => this.onclose(ev)
        ws.onerror = (ev: Event) => this.onerror(ev)
        ws.onmessage = (ev: MessageEvent) => this.onmessage(ev)
        resolve()
      }
    })
  }

  subscribe<D, M>(subscription: WSSubscription<D, M>) {
    const index = this.subscriptions.push(
      subscription as WSSubscription<unknown, unknown>
    )
    return () => this.subscriptions.splice(index - 1, 1)
  }

  isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  close() {
    this.lastCloseEvent = null
    this.lastInWSEvent = null
    this.lastOutWSEvent = null
    // This is only for web
    // this.detachOnFocus()
    this.ws?.close()
    this.ws = undefined
  }

  private onopen(ev: Event) {
    this.lastCloseEvent = null
    this.onWSOpen?.call(this, ev)
  }

  private onmessage(ev: MessageEvent) {
    this.onWSMessage?.call(this, ev)
    const wsEvent = JSON.parse(ev.data) as WSEvent<unknown, unknown>
    this.lastInWSEvent = new EventWrapper(wsEvent)
    for (const subscription of this.subscriptions) {
      try {
        subscription(wsEvent)
      } catch (e) {
        console.error(e)
      }
    }
  }

  protected async tryReconnect() {
    try {
      if (this.reconnectHandler) {
        const userOrPromise = await this.reconnectHandler.call(this, this)
        if (userOrPromise) {
          await this.reconnect(userOrPromise)
        } else {
          console.error('ws reconnect rejected by reconnectHandler')
        }
      } else {
        await this.reconnect()
      }
    } catch (e) {
      console.error(e)
    }
  }

  public async reconnect(promise?: WsUser | Promise<WsUser>) {
    if (this.reconnectRetries > this.reconnectRetriesLimit) {
      return Promise.reject('Max reconnect retries reached')
    }
    this.reconnectRetries++
    const user = await promise
    if (user?.id && user.id !== this.user?.id) {
      this.user = user
    }
    return this.doConnect()
  }

  private onclose(ev: CloseEvent) {
    this.lastCloseEvent = new EventWrapper<CloseEvent>(ev)
    if (ev.code === 1006) {
      // Abnormal Closure
      this.tryReconnect()
        .then()
        .catch((e) => console.error(e))
    }
    this.onWSClose?.call(this, ev)
  }

  private onerror(ev: Event) {
    this.onWSError?.call(this, ev)
  }

  sendPlain(data: string | ArrayBufferLike | Blob | ArrayBufferView) {
    this.ws?.send(data)
  }

  send(e: WSEvent<unknown, unknown>) {
    this.lastOutWSEvent = new EventWrapper(e)
    this.sendPlain(JSON.stringify(e))
  }
}
